import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import StatusBadge from '../components/StatusBadge';
import RazorpayButton from '../components/RazorpayButton';
import PageTitle from '../components/PageTitle';

const API_BASE = process.env.REACT_APP_API_URL || '';

// ── Progress trackers ─────────────────────────────────────────
const ITR_STEPS = [
  { key: 'SUBMITTED',       label: 'Submitted' },
  { key: 'UNDER_REVIEW',    label: 'Review' },
  { key: 'ACTION_REQUIRED', label: 'Action' },
  { key: 'DRAFT_SENT',      label: 'Draft' },
  { key: 'APPROVED',        label: 'Approved' },
  { key: 'FILED',           label: 'Filed' },
  { key: 'ACKNOWLEDGED',    label: 'Done' },
];

const GST_STEPS = [
  { key: 'SUBMITTED',          label: 'Submitted' },
  { key: 'DOCUMENTS_VERIFIED', label: 'Verified' },
  { key: 'APPLICATION_FILED',  label: 'Filed' },
  { key: 'GSTIN_ASSIGNED',     label: 'GSTIN' },
];

const DSC_STEPS = [
  { key: 'APPLIED',                    label: 'Applied' },
  { key: 'DOCUMENTS_VERIFIED',         label: 'Verified' },
  { key: 'SUBMITTED_TO_PROVIDER',      label: 'Provider' },
  { key: 'VIDEO_VERIFICATION_PENDING', label: 'Video' },
  { key: 'ISSUED',                     label: 'Issued' },
];

const GST_STATUS_LABEL = {
  SUBMITTED:             { label: 'Submitted',            cls: 'badge-submitted' },
  DOCUMENTS_VERIFIED:    { label: 'Docs Verified',        cls: 'badge-approved' },
  APPLICATION_FILED:     { label: 'Filed on Portal',      cls: 'badge-filed' },
  PENDING_CLARIFICATION: { label: 'Clarification Needed', cls: 'badge-under_review' },
  GSTIN_ASSIGNED:        { label: 'GSTIN Assigned',       cls: 'badge-acknowledged' },
  REJECTED:              { label: 'Rejected',             cls: 'badge-rejected' },
};

const DSC_STATUS_LABEL = {
  APPLIED:                    { label: 'Applied',            cls: 'badge-submitted' },
  DOCUMENTS_VERIFIED:         { label: 'Docs Verified',      cls: 'badge-approved' },
  SUBMITTED_TO_PROVIDER:      { label: 'With Provider',      cls: 'badge-under_review' },
  VIDEO_VERIFICATION_PENDING: { label: 'Video Verification', cls: 'badge-draft_sent' },
  ISSUED:                     { label: 'Issued',             cls: 'badge-acknowledged' },
  REJECTED:                   { label: 'Rejected',           cls: 'badge-rejected' },
};

function ProgressTracker({ steps, currentStatus }) {
  const idx = steps.findIndex(s => s.key === currentStatus);
  return (
    <div className="progress-track">
      {steps.map((s, i) => {
        const state = i < idx ? 'done' : i === idx ? 'active' : 'pending';
        return (
          <div key={s.key} className={`pt-step ${state}`}>
            <div className="pt-circle">{state === 'done' ? '✓' : i + 1}</div>
            <div className="pt-label">{s.label}</div>
          </div>
        );
      })}
    </div>
  );
}

// ── Payment Due Card ──────────────────────────────────────────
function PaymentDueCard({ payment, onPaid }) {
  const [paid, setPaid] = useState(false);

  if (paid) return (
    <div className="card" style={{ border: '2px solid var(--green)', background: 'var(--greenl)', textAlign: 'center', padding: '1.5rem' }}>
      <div style={{ fontSize: '2rem', marginBottom: '.5rem' }}>🎉</div>
      <div style={{ fontWeight: 700, color: 'var(--green)', fontSize: '1rem' }}>Payment Successful!</div>
      <div style={{ fontSize: '.85rem', color: 'var(--muted)', marginTop: '.25rem' }}>{payment.serviceDesc}</div>
    </div>
  );

  return (
    <div className="card" style={{ border: '2px solid var(--gold)', background: '#FFFBF0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '.6rem', marginBottom: '.75rem' }}>
        <span style={{ fontSize: '1.3rem' }}>💳</span>
        <div>
          <div style={{ fontWeight: 700, color: 'var(--navy)', fontSize: '.92rem' }}>Payment Due</div>
          <div style={{ fontSize: '.78rem', color: 'var(--muted)' }}>Invoice {payment.invoiceNumber}</div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem', flexWrap: 'wrap', gap: '.5rem' }}>
        <div>
          <div style={{ fontSize: '.88rem', color: 'var(--navy)', fontWeight: 500 }}>{payment.serviceDesc}</div>
          {payment.adminNote && (
            <div style={{ fontSize: '.8rem', color: 'var(--muted)', fontStyle: 'italic', marginTop: '.2rem' }}>"{payment.adminNote}"</div>
          )}
          <div style={{ fontSize: '.75rem', color: 'var(--muted)', marginTop: '.25rem' }}>
            Fee: ₹{Number(payment.amount).toLocaleString('en-IN')} + GST 18%: ₹{Number(payment.gstAmount).toLocaleString('en-IN')}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.6rem', color: 'var(--navy)', lineHeight: 1 }}>
            ₹{Number(payment.totalAmount).toLocaleString('en-IN')}
          </div>
          <div style={{ fontSize: '.68rem', color: 'var(--muted)' }}>total incl. GST</div>
        </div>
      </div>

      <RazorpayButton
        paymentId={payment.id}
        amount={`₹${Number(payment.totalAmount).toLocaleString('en-IN')}`}
        serviceDesc={payment.serviceDesc}
        onSuccess={verified => { setPaid(true); if (onPaid) onPaid(verified); }}
      />
    </div>
  );
}

// ── Main Dashboard ────────────────────────────────────────────
export default function Dashboard() {
  const { user }  = useAuth();
  const navigate  = useNavigate();

  const [filings,    setFilings]    = useState([]);
  const [gstApps,    setGstApps]    = useState([]);
  const [gstReturns, setGstReturns] = useState([]);
  const [dscApps,    setDscApps]    = useState([]);
  const [payments,   setPayments]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [tab,        setTab]        = useState('all');

  const load = () =>
    Promise.all([
      api.get('/api/filing/my'),
      api.get('/api/gst/my'),
      api.get('/api/gst-return/my'),
      api.get('/api/dsc/my'),
      api.get('/api/payment/my'),
    ]).then(([f, g, gr, dsc, pay]) => {
      setFilings(f.data);
      setGstApps(g.data);
      setGstReturns(gr.data);
      setDscApps(dsc.data);
      setPayments(pay.data);
    }).catch(console.error).finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  // Pending payments — show at top
  const pendingPayments = payments.filter(p => p.status === 'PENDING' || p.status === 'INITIATED');

  // All applications combined and sorted
  const allItems = [
    ...filings.map(f    => ({ ...f,  _type: 'itr' })),
    ...gstApps.map(g    => ({ ...g,  _type: 'gst' })),
    ...gstReturns.map(r => ({ ...r,  _type: 'gst-return' })),
    ...dscApps.map(d    => ({ ...d,  _type: 'dsc' })),
  ].sort((a, b) => new Date(b.submittedAt || b.appliedAt) - new Date(a.submittedAt || a.appliedAt));

  const displayed =
    tab === 'itr'        ? filings.map(f    => ({ ...f,  _type: 'itr' })) :
    tab === 'gst'        ? gstApps.map(g    => ({ ...g,  _type: 'gst' })) :
    tab === 'gst-return' ? gstReturns.map(r => ({ ...r,  _type: 'gst-return' })) :
    tab === 'dsc'        ? dscApps.map(d    => ({ ...d,  _type: 'dsc' })) :
    tab === 'payments'   ? [] : // payments rendered separately
    allItems;

  return (
    <>
      <PageTitle title="Dashboard" />
      <div className="page-hero">
        <div className="page-hero-label">Dashboard</div>
        <h1>Welcome back, {user?.fullName?.split(' ')[0]} 👋</h1>
        <p>All your filings, applications and payments in one place</p>
      </div>

      <div className="page-wide">

        {/* ── Stats ── */}
        <div className="stat-grid" style={{ marginBottom: '1.5rem' }}>
          {[
            { icon: '📋', label: 'ITR Filings',   value: filings.length },
            { icon: '🧾', label: 'GST',           value: gstApps.length },
            { icon: '📊', label: 'GST Returns',   value: gstReturns.length },
            { icon: '🔐', label: 'DSC',           value: dscApps.length },
            { icon: '💳', label: 'Pending Pay',   value: pendingPayments.length, highlight: pendingPayments.length > 0 },
          ].map(s => (
            <div key={s.label} className="stat-card"
              style={s.highlight ? { border: '2px solid var(--gold)', background: '#FFFBF0' } : {}}>
              <div style={{ fontSize: '1.4rem', marginBottom: '.2rem' }}>{s.icon}</div>
              <div className="stat-num" style={s.highlight ? { color: 'var(--gold)' } : {}}>{s.value}</div>
              <div className="stat-label">{s.label}</div>
            </div>
          ))}
        </div>

        {/* ── Pending payments banner ── */}
        {pendingPayments.length > 0 && (
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{ fontSize: '.72rem', textTransform: 'uppercase', letterSpacing: '.1em', color: 'var(--gold)', fontWeight: 600, marginBottom: '.75rem' }}>
              ⚠️ Payment Required ({pendingPayments.length})
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '.75rem' }}>
              {pendingPayments.map(p => (
                <PaymentDueCard
                  key={p.id}
                  payment={p}
                  onPaid={() => load()} // refresh after payment
                />
              ))}
            </div>
          </div>
        )}

        {/* ── New application buttons ── */}
        <div style={{ display: 'flex', gap: '.75rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
          <Link to="/file">       <button className="btn btn-primary btn-sm">📋 File ITR</button></Link>
          <Link to="/gst">        <button className="btn btn-outline btn-sm">🧾 GST Reg.</button></Link>
          <Link to="/gst-return"> <button className="btn btn-outline btn-sm">📊 GST Return</button></Link>
          <Link to="/dsc">        <button className="btn btn-outline btn-sm">🔐 Apply DSC</button></Link>
        </div>

        {/* ── Tabs ── */}
        <div style={{ display: 'flex', gap: '.4rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
          {[
            ['all',        `All (${allItems.length})`],
            ['itr',        `ITR (${filings.length})`],
            ['gst',        `GST Reg. (${gstApps.length})`],
            ['gst-return', `GST Returns (${gstReturns.length})`],
            ['dsc',        `DSC (${dscApps.length})`],
            ['payments',   `Payments (${payments.length})`],
          ].map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)}
              style={{ padding: '.4rem .9rem', borderRadius: 8, border: `1.5px solid ${tab === key ? 'var(--navy)' : 'var(--border)'}`, background: tab === key ? 'var(--navy)' : '#fff', color: tab === key ? '#fff' : 'var(--muted)', fontFamily: 'DM Sans, sans-serif', fontSize: '.8rem', fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              {label}
            </button>
          ))}
        </div>

        {/* ── Content ── */}
        {loading ? (
          <div className="centered"><span className="spinner spinner-dark" /></div>
        ) : tab === 'payments' ? (
          <PaymentsHistory payments={payments} onRetry={() => load()} />
        ) : displayed.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>📂</div>
            <h3 style={{ color: 'var(--navy)', marginBottom: '.5rem', fontSize: '1rem' }}>No applications yet</h3>
            <p style={{ color: 'var(--muted)', fontSize: '.88rem' }}>Use the buttons above to get started</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {displayed.map(item =>
              item._type === 'itr'        ? <ItrCard        key={`itr-${item.id}`}   filing={item}  navigate={navigate} /> :
              item._type === 'gst'        ? <GstCard        key={`gst-${item.id}`}   app={item}     navigate={navigate} /> :
              item._type === 'gst-return' ? <GstReturnCard  key={`gstr-${item.id}`}  ret={item}     navigate={navigate} /> :
                                            <DscCard         key={`dsc-${item.id}`}   app={item}     navigate={navigate} />
            )}
          </div>
        )}
      </div>
    </>
  );
}

// ── Payments History Tab ──────────────────────────────────────
function PaymentsHistory({ payments, onRetry }) {
  const STATUS = {
    PENDING:   { label: 'Pending',   icon: '⏳', color: '#E65100',      bg: '#FFF3E0' },
    INITIATED: { label: 'Initiated', icon: '🔄', color: 'var(--blue)',   bg: 'var(--bluel)' },
    PAID:      { label: 'Paid ✅',   icon: '✅', color: 'var(--green)',  bg: 'var(--greenl)' },
    FAILED:    { label: 'Failed',    icon: '❌', color: 'var(--red)',    bg: 'var(--redl)' },
    REFUNDED:  { label: 'Refunded', icon: '↩️', color: 'var(--muted)',  bg: 'var(--creamd)' },
  };

  if (payments.length === 0) return (
    <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
      <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>💳</div>
      <h3 style={{ color: 'var(--navy)', marginBottom: '.5rem', fontSize: '1rem' }}>No payments yet</h3>
      <p style={{ color: 'var(--muted)', fontSize: '.88rem' }}>Payments will appear here once your CA sets a price for your service</p>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '.75rem' }}>
      {payments.map(p => {
        const s = STATUS[p.status] || STATUS.PENDING;
        return (
          <div key={p.id} className="card card-sm">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '.75rem' }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', marginBottom: '.25rem' }}>
                  <span style={{ background: s.bg, color: s.color, fontSize: '.72rem', fontWeight: 700, padding: '.15rem .55rem', borderRadius: 20 }}>
                    {s.icon} {s.label}
                  </span>
                  <span style={{ fontFamily: 'monospace', fontSize: '.72rem', color: 'var(--muted)' }}>{p.invoiceNumber}</span>
                </div>
                <div style={{ fontWeight: 600, fontSize: '.9rem', color: 'var(--navy)' }}>{p.serviceDesc}</div>
                {p.adminNote && <div style={{ fontSize: '.78rem', color: 'var(--muted)', marginTop: '.15rem', fontStyle: 'italic' }}>"{p.adminNote}"</div>}
                <div style={{ fontSize: '.75rem', color: 'var(--muted)', marginTop: '.35rem' }}>
                  Fee: ₹{Number(p.amount || 0).toLocaleString('en-IN')} + GST: ₹{Number(p.gstAmount || 0).toLocaleString('en-IN')}
                  {p.razorpayPaymentId && <span style={{ marginLeft: '.5rem' }}>· ID: {p.razorpayPaymentId}</span>}
                </div>
                <div style={{ fontSize: '.72rem', color: 'var(--muted)', marginTop: '.2rem' }}>
                  {new Date(p.createdAt).toLocaleString('en-IN')}
                  {p.paidAt && p.status === 'PAID' && <span> · Paid: {new Date(p.paidAt).toLocaleString('en-IN')}</span>}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.4rem', color: p.status === 'PAID' ? 'var(--green)' : 'var(--navy)' }}>
                  ₹{Number(p.totalAmount || 0).toLocaleString('en-IN')}
                </div>
                {/* Retry payment if pending */}
                {(p.status === 'PENDING' || p.status === 'FAILED') && (
                  <div style={{ marginTop: '.5rem' }}>
                    <RazorpayButton
                      paymentId={p.id}
                      amount={`₹${Number(p.totalAmount).toLocaleString('en-IN')}`}
                      serviceDesc={p.serviceDesc}
                      onSuccess={() => onRetry()}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Application Cards ─────────────────────────────────────────

function ItrCard({ filing: f, navigate }) {
  const [approving, setApproving] = useState(false);

  const approve = async e => {
    e.stopPropagation();
    setApproving(true);
    try { await api.post(`/api/filing/${f.id}/approve`); window.location.reload(); }
    catch { alert('Failed'); } finally { setApproving(false); }
  };

  const download = async (filename, name, e) => {
    e.stopPropagation();
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_BASE}/api/files/download/${filename}`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = name;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  return (
    <div className="card"
      style={{ cursor: 'pointer', border: (f.status === 'DRAFT_SENT' || f.status === 'ACTION_REQUIRED') ? '2px solid var(--gold)' : '1px solid var(--border)' }}
      onClick={() => navigate(`/applications/itr/${f.id}`)}
      onMouseOver={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(11,31,58,.08)'}
      onMouseOut={e => e.currentTarget.style.boxShadow = 'none'}>

      {f.status === 'DRAFT_SENT' && (
        <div style={{ background: 'var(--gold)', color: 'var(--navy)', padding: '.3rem 1rem', margin: '-1.5rem -1.5rem 1rem', borderRadius: '14px 14px 0 0', fontSize: '.75rem', fontWeight: 600 }}>
          ⚠️ Action Required — Review and approve your draft ITR
        </div>
      )}
      {f.status === 'ACTION_REQUIRED' && (
        <div style={{ background: '#E65100', color: '#fff', padding: '.3rem 1rem', margin: '-1.5rem -1.5rem 1rem', borderRadius: '14px 14px 0 0', fontSize: '.75rem', fontWeight: 600 }}>
          ⚠️ Action Required — Your CA needs additional information or corrections
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '.9rem' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', marginBottom: '.2rem' }}>
            <span style={{ background: 'var(--bluel)', color: 'var(--blue)', fontSize: '.68rem', fontWeight: 700, padding: '.12rem .45rem', borderRadius: 4 }}>ITR</span>
            <span style={{ fontFamily: 'Playfair Display, serif', fontSize: '.95rem', color: 'var(--navy)' }}>{f.itrType} — AY {f.assessmentYr}</span>
          </div>
          <div style={{ fontSize: '.75rem', color: 'var(--muted)' }}>#{f.id} · {f.pan} · ₹{Number(f.annualIncome || 0).toLocaleString('en-IN')}</div>
        </div>
        <StatusBadge status={f.status} />
      </div>

      {f.status !== 'REJECTED' ? <ProgressTracker steps={ITR_STEPS} currentStatus={f.status} /> :
        <div className="alert alert-error" style={{ fontSize: '.82rem' }}>❌ {f.adminNote || 'Filing rejected'}</div>}

      {f.adminNote && f.status !== 'REJECTED' && (
        <div style={{ marginTop: '.6rem', background: 'var(--bluel)', border: '1px solid rgba(26,95,180,.15)', borderRadius: 8, padding: '.5rem .75rem', fontSize: '.8rem', color: 'var(--blue)' }}>
          💬 {f.adminNote}
        </div>
      )}

      {(f.status === 'DRAFT_SENT' || f.status === 'ACKNOWLEDGED') && (
        <div style={{ marginTop: '.85rem', display: 'flex', gap: '.6rem', flexWrap: 'wrap' }} onClick={e => e.stopPropagation()}>
          {f.status === 'DRAFT_SENT' && (
            <>
              {f.docDraftItr && <button className="btn btn-outline btn-sm" onClick={e => download(f.docDraftItr, 'Draft_ITR.pdf', e)}>⬇ Draft ITR</button>}
              <button className="btn btn-gold btn-sm" onClick={approve} disabled={approving}>
                {approving ? 'Approving...' : '✅ Approve Draft'}
              </button>
            </>
          )}
          {f.status === 'ACKNOWLEDGED' && f.docItrV && (
            <button className="btn btn-primary btn-sm" onClick={e => download(f.docItrV, 'ITR-V.pdf', e)}>⬇ Download ITR-V</button>
          )}
        </div>
      )}

      <div style={{ fontSize: '.7rem', color: 'var(--muted)', marginTop: '.5rem' }}>
        {new Date(f.submittedAt).toLocaleDateString('en-IN')} · Tap for details
      </div>
    </div>
  );
}

function GstCard({ app: g, navigate }) {
  const s = GST_STATUS_LABEL[g.status] || { label: g.status, cls: '' };
  const download = async (filename, name, e) => {
    e.stopPropagation();
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_BASE}/api/files/download/${filename}`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = name;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  return (
    <div className="card"
      style={{ cursor: 'pointer', border: g.status === 'PENDING_CLARIFICATION' ? '2px solid var(--gold)' : '1px solid var(--border)' }}
      onClick={() => navigate(`/applications/gst/${g.id}`)}
      onMouseOver={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(11,31,58,.08)'}
      onMouseOut={e => e.currentTarget.style.boxShadow = 'none'}>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '.9rem' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', marginBottom: '.2rem' }}>
            <span style={{ background: 'var(--greenl)', color: 'var(--green)', fontSize: '.68rem', fontWeight: 700, padding: '.12rem .45rem', borderRadius: 4 }}>GST</span>
            <span style={{ fontFamily: 'Playfair Display, serif', fontSize: '.95rem', color: 'var(--navy)' }}>{g.businessName}</span>
          </div>
          <div style={{ fontSize: '.75rem', color: 'var(--muted)' }}>
            #{g.id} · {g.businessType} · {g.pan}
            {g.gstin && <span style={{ marginLeft: '.5rem', color: 'var(--green)', fontWeight: 600 }}>GSTIN: {g.gstin}</span>}
          </div>
        </div>
        <span className={`badge ${s.cls}`}>{s.label}</span>
      </div>

      {g.status !== 'REJECTED' ? <ProgressTracker steps={GST_STEPS} currentStatus={g.status} /> :
        <div className="alert alert-error" style={{ fontSize: '.82rem' }}>❌ {g.adminNote || 'Application rejected'}</div>}

      {g.adminNote && g.status !== 'REJECTED' && (
        <div style={{ marginTop: '.6rem', background: 'var(--greenl)', border: '1px solid #a8d5b9', borderRadius: 8, padding: '.5rem .75rem', fontSize: '.8rem', color: 'var(--green)' }}>
          💬 {g.adminNote}
        </div>
      )}

      {g.status === 'GSTIN_ASSIGNED' && g.docGstCertificate && (
        <div style={{ marginTop: '.85rem' }} onClick={e => e.stopPropagation()}>
          <button className="btn btn-primary btn-sm" onClick={e => download(g.docGstCertificate, 'GST_Certificate.pdf', e)}>
            ⬇ Download GST Certificate
          </button>
        </div>
      )}

      <div style={{ fontSize: '.7rem', color: 'var(--muted)', marginTop: '.5rem' }}>{new Date(g.submittedAt).toLocaleDateString('en-IN')} · Tap for details</div>
    </div>
  );
}

function GstReturnCard({ ret: r, navigate }) {
  const STATUS = { SUBMITTED: { label: 'Submitted', cls: 'badge-submitted' }, UNDER_REVIEW: { label: 'Under Review', cls: 'badge-under_review' }, FILED: { label: 'Filed', cls: 'badge-filed' } };
  const s = STATUS[r.status] || { label: r.status, cls: '' };
  const STEPS = [{key:'SUBMITTED',label:'Submitted'},{key:'UNDER_REVIEW',label:'Review'},{key:'FILED',label:'Filed'}];

  const download = async (filename, name, e) => {
    e.stopPropagation();
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_BASE}/api/files/download/${filename}`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = name;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  return (
    <div className="card"
      style={{ cursor: 'pointer', border: '1px solid var(--border)' }}
      onClick={() => navigate(`/applications/gst-return/${r.id}`)}
      onMouseOver={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(11,31,58,.08)'}
      onMouseOut={e => e.currentTarget.style.boxShadow = 'none'}>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '.9rem' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', marginBottom: '.2rem' }}>
            <span style={{ background: '#EDE7F6', color: '#512DA8', fontSize: '.68rem', fontWeight: 700, padding: '.12rem .45rem', borderRadius: 4 }}>GST RETURN</span>
            <span style={{ fontFamily: 'Playfair Display, serif', fontSize: '.95rem', color: 'var(--navy)' }}>{r.returnType} — {r.filingPeriod}</span>
          </div>
          <div style={{ fontSize: '.75rem', color: 'var(--muted)' }}>#{r.id} · {r.gstin} · {r.businessName}</div>
        </div>
        <span className={`badge ${s.cls}`}>{s.label}</span>
      </div>

      <ProgressTracker steps={STEPS} currentStatus={r.status} />

      {r.adminNote && (
        <div style={{ marginTop: '.6rem', background: '#EDE7F6', border: '1px solid #CE93D8', borderRadius: 8, padding: '.5rem .75rem', fontSize: '.8rem', color: '#512DA8' }}>
          💬 {r.adminNote}
        </div>
      )}

      {r.status === 'FILED' && r.docFiledReturn && (
        <div style={{ marginTop: '.85rem' }} onClick={e => e.stopPropagation()}>
          <button className="btn btn-primary btn-sm" onClick={e => download(r.docFiledReturn, 'Filed_Return.pdf', e)}>
            ⬇ Download Filed Return
          </button>
        </div>
      )}

      <div style={{ fontSize: '.7rem', color: 'var(--muted)', marginTop: '.5rem' }}>{new Date(r.submittedAt).toLocaleDateString('en-IN')} · Tap for details</div>
    </div>
  );
}

function DscCard({ app: d, navigate }) {
  const s = DSC_STATUS_LABEL[d.status] || { label: d.status, cls: '' };
  const STEPS_ORDER = ['APPLIED','DOCUMENTS_VERIFIED','SUBMITTED_TO_PROVIDER','VIDEO_VERIFICATION_PENDING','ISSUED'];

  const download = async (filename, name, e) => {
    e.stopPropagation();
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_BASE}/api/files/download/${filename}`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = name;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  return (
    <div className="card"
      style={{ cursor: 'pointer', border: '1px solid var(--border)' }}
      onClick={() => navigate(`/applications/dsc/${d.id}`)}
      onMouseOver={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(11,31,58,.08)'}
      onMouseOut={e => e.currentTarget.style.boxShadow = 'none'}>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '.9rem' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', marginBottom: '.2rem' }}>
            <span style={{ background: '#E8EAF6', color: '#3949AB', fontSize: '.68rem', fontWeight: 700, padding: '.12rem .45rem', borderRadius: 4 }}>DSC</span>
            <span style={{ fontFamily: 'Playfair Display, serif', fontSize: '.95rem', color: 'var(--navy)' }}>{d.dscType}</span>
          </div>
          <div style={{ fontSize: '.75rem', color: 'var(--muted)' }}>
            #{d.id} · {d.validity} · {d.tokenType}
            {d.serialNumber && <span style={{ marginLeft: '.5rem', color: 'var(--green)', fontWeight: 600 }}>S/N: {d.serialNumber}</span>}
          </div>
        </div>
        <span className={`badge ${s.cls}`}>{s.label}</span>
      </div>

      {d.status !== 'REJECTED' ? <ProgressTracker steps={DSC_STEPS} currentStatus={d.status} /> :
        <div className="alert alert-error" style={{ fontSize: '.82rem' }}>❌ {d.adminNote || 'Application rejected'}</div>}

      {d.status === 'VIDEO_VERIFICATION_PENDING' && (
        <div style={{ marginTop: '.6rem', background: '#FFF8E1', border: '1px solid var(--gold)', borderRadius: 8, padding: '.5rem .75rem', fontSize: '.8rem', color: '#5D4037' }}>
          🎥 Our team will call {d.phone} to schedule your video verification.
        </div>
      )}

      {d.adminNote && !['REJECTED','VIDEO_VERIFICATION_PENDING'].includes(d.status) && (
        <div style={{ marginTop: '.6rem', background: '#EDE7F6', border: '1px solid #CE93D8', borderRadius: 8, padding: '.5rem .75rem', fontSize: '.8rem', color: '#4527A0' }}>
          💬 {d.adminNote}
        </div>
      )}

      {d.status === 'ISSUED' && d.docDscDetails && (
        <div style={{ marginTop: '.85rem' }} onClick={e => e.stopPropagation()}>
          <button className="btn btn-primary btn-sm" onClick={e => download(d.docDscDetails, 'DSC_Details.pdf', e)}>
            ⬇ Download DSC Details
          </button>
        </div>
      )}

      <div style={{ fontSize: '.7rem', color: 'var(--muted)', marginTop: '.5rem' }}>{new Date(d.appliedAt).toLocaleDateString('en-IN')} · Tap for details</div>
    </div>
  );
}